import {
    Autocomplete, Box, Grid, IconButton, InputAdornment, Table, TableBody,
    TableCell, TableContainer, TableHead, TablePagination, TableRow,
    TextField, Tooltip, Typography
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import SyncAltIcon from '@mui/icons-material/SyncAlt'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import PopupDialog from '../../Components/Popups/ConfirmDialog'
import { ApiGetAutoTransaction } from '../../apiService/Core/Apicall'
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom'


interface SkriptTransaction {
    _id: string
    skript_transaction_id: string
    accountId: string | null
    reference: string | null
    amount: string | null
    currency: string
    depositedCurrency?: string
    depositedAmount?: string
    type: string | null
    transactionStatus: string | null
    description: string | null
    postingDateTime: string | null
    dataHolderName: string | null
    modifiedDate: string | null
    userId: string | { _id: string; firstName: string; lastName: string; email: string } | null
    status: 'PENDING' | 'COMPLETED' | 'FAILED' | 'NO_USER' | 'SKIPPED'
    errorMessage: string | null
    processedAt: string | null
    createdAt: string
}

interface Pagination {
    total: number; page: number; limit: number; totalPages: number
    hasNextPage: boolean; hasPrevPage: boolean
}

interface Stats {
    totalAmount: number; pendingCount: number; completedCount: number; failedCount: number
}

const fmtDate = (d?: string | null) =>
    d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'

const fmtTime = (d?: string | null) =>
    d ? new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : ''

const fmtAmt = (v?: string | number | null) => {
    const n = Number(v)
    return isNaN(n) ? '—' : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const getUserInfo = (userId: SkriptTransaction['userId']) => {
    if (typeof userId === 'object' && userId !== null)
        return { name: `${userId.firstName} ${userId.lastName}`, email: userId.email }
    return { name: '—', email: '—' }
}

const STATUS_COLORS: Record<string, { color: string; bg: string; border: string }> = {
    COMPLETED: { color: '#10b981', bg: '#10b98118', border: '#10b98150' },
    PENDING: { color: '#f59e0b', bg: '#f59e0b18', border: '#f59e0b50' },
    FAILED: { color: '#ef4444', bg: '#ef444418', border: '#ef444450' },
    NO_USER: { color: '#3b82f6', bg: '#3b82f618', border: '#3b82f650' },
    SKIPPED: { color: '#9a9a9a', bg: '#9a9a9a18', border: '#9a9a9a50' },
}

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
    const s = STATUS_COLORS[status] || STATUS_COLORS.SKIPPED
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.6,
            px: 1.5, py: 0.4, borderRadius: '6px', fontSize: '11px', fontWeight: 700,
            color: s.color, backgroundColor: s.bg, border: `1px solid ${s.border}`,
            letterSpacing: '0.03em', whiteSpace: 'nowrap',
        }}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
            {status}
        </Box>
    )
}

const StatCard: React.FC<{ label: string; value: string; sub?: string; color: string; icon: React.ReactNode }> = ({ label, value, sub, color, icon }) => (
    <Box sx={{
        background: 'linear-gradient(135deg, #2a2a2a 0%, #222 100%)',
        border: '1px solid #333', borderRadius: '14px', p: 2.5, position: 'relative', overflow: 'hidden',
        transition: 'border-color 0.25s', '&:hover': { borderColor: color + '60' },
        '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '2px', background: `linear-gradient(90deg, ${color}00, ${color}, ${color}00)` },
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography fontSize={10} color="#6f6f6f" textTransform="uppercase" letterSpacing="0.1em">{label}</Typography>
            <Box sx={{ color, opacity: 0.7 }}>{icon}</Box>
        </Box>
        <Typography fontSize={20} fontWeight={800} color={color} sx={{ letterSpacing: '-0.02em' }}>{value}</Typography>
        {sub && <Typography fontSize={10} color="#5a5a5a" mt={0.5}>{sub}</Typography>}
    </Box>
)

const STATUS_OPTIONS = ['PENDING', 'COMPLETED', 'FAILED', 'NO_USER', 'SKIPPED']

const AutoTransfers: React.FC = () => {

    const [transactions, setTransactions] = useState<SkriptTransaction[]>([])
    const [pagination, setPagination] = useState<Pagination>({
        total: 0, page: 1, limit: 10, totalPages: 1, hasNextPage: false, hasPrevPage: false
    })
    const [stats, setStats] = useState<Stats>({
        totalAmount: 0, pendingCount: 0, completedCount: 0, failedCount: 0
    })
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [status, setStatus] = useState('')
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [errorPopup, setErrorPopup] = useState<{ open: boolean; message: string }>({ open: false, message: '' })
    const navigate = useNavigate()

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 500)
        return () => clearTimeout(t)
    }, [search])

    const fetchTransactions = useCallback(async () => {
        try {
            const params = new URLSearchParams()
            params.append('page', String(page + 1))
            params.append('limit', String(rowsPerPage))
            if (debouncedSearch) params.append('search', debouncedSearch)
            if (status) params.append('status', status)

            const res = await ApiGetAutoTransaction(`?${params.toString()}`)
            if (res?.status) {
                setTransactions(res.data?.transactions || [])
                setPagination(res.data?.pagination || {})
                setStats(res.data?.stats || { totalAmount: 0, pendingCount: 0, completedCount: 0, failedCount: 0 })
            }
        } catch (error: any) {
            setErrorPopup({ open: true, message: error?.message || 'Failed to fetch transactions' })
        }
    }, [page, rowsPerPage, debouncedSearch, status])

    useEffect(() => { fetchTransactions() }, [fetchTransactions])

    return (
        <div className='common-layout flex-column'>
            <Box className="border-10 p-2 px-4 mixed-orange mb-4 both-circle d-flex align-items-center justify-content-between">
                <Box className="d-flex gap-3 align-items-center">
                    <Box className="d-flex align-items-center gap-2">
                        <IconButton sx={{ ':hover': { backgroundColor: '#ffffff1A' } }} onClick={() => navigate('/admin/transfers')} >
                            <ArrowBackIcon sx={{ color: "#ffffff" }} />
                        </IconButton>
                        <Typography className='fs-24 text-white fw-medium' sx={{ lineHeight: 1.1 }}>Auto  Transactions</Typography>
                    </Box>
                </Box>
            </Box>

            <Grid container spacing={2} mb={3}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        label="Total Amount"
                        value={`$${fmtAmt(stats.totalAmount)}`}
                        sub="Sum of all transactions"
                        color="#10b981"
                        icon={<AccountBalanceWalletOutlinedIcon fontSize="small" />}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        label="Completed"
                        value={String(stats.completedCount)}
                        sub="Successfully processed"
                        color="#10b981"
                        icon={<CheckCircleOutlineIcon fontSize="small" />}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        label="Pending"
                        value={String(stats.pendingCount)}
                        sub="Awaiting processing"
                        color="#f59e0b"
                        icon={<HourglassEmptyIcon fontSize="small" />}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        label="Failed"
                        value={String(stats.failedCount)}
                        sub="Could not be processed"
                        color="#ef4444"
                        icon={<ErrorOutlineIcon fontSize="small" />}
                    />
                </Grid>
            </Grid>

            <Box className="bg-dark-color border-10 p-4">

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography fontSize={14} fontWeight={700} color="#9a9a9a">
                        {pagination.total} transaction{pagination.total !== 1 ? 's' : ''} found
                    </Typography>
                    <Box className="d-flex align-items-center gap-2" sx={{ flexWrap: 'wrap' }}>
                        <Autocomplete
                            size="small"
                            value={status}
                            disableClearable={!status}
                            onChange={(_, v) => { setStatus(v || ''); setPage(0) }}
                            options={STATUS_OPTIONS}
                            sx={{ width: 180 }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Status"
                                    sx={searchFieldStyle}
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: { color: '#fff', '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: '#fff' } }
                                    }}
                                />
                            )}
                            PaperComponent={({ children }) => (
                                <Box className="bg-dark-color" sx={{
                                    color: '#fff',
                                    '& .MuiAutocomplete-option': {
                                        '&:hover': { bgcolor: '#241815' },
                                        "&[aria-selected='true']": { bgcolor: '#241815' }
                                    }
                                }}>
                                    {children}
                                </Box>
                            )}
                        />
                        <TextField
                            placeholder="Search by user..."
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(0) }}
                            size="small"
                            sx={{ ...searchFieldStyle, width: 300 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#f2674a', fontSize: 18 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                </Box>

                <TableContainer sx={{ maxHeight: '55vh', overflow: 'auto', borderRadius: '10px', border: '1px solid #2f2f2f' }}>
                    <Table stickyHeader sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #1e1e1e' } }}>
                        <TableHead>
                            <TableRow>
                                {['#', 'User', 'Transaction ID', 'Reference', 'Amount (AUD)', 'Deposited (USD)', 'Type', 'Data Holder', 'Posting Date', 'Processed At', 'Status', 'Error'].map(h => (
                                    <TableCell key={h} sx={thStyle}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {transactions.length > 0 ? transactions.map((tx, i) => (
                                <TableRow key={tx._id} sx={trHover}>

                                    <TableCell sx={tdStyle}>{page * rowsPerPage + i + 1}</TableCell>

                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={13} fontWeight={600} color="#e0e0e0">{getUserInfo(tx.userId).name}</Typography>
                                        <Typography fontSize={10} color="#5a5a5a">{getUserInfo(tx.userId).email}</Typography>
                                    </TableCell>

                                    <TableCell sx={tdStyle}>
                                        <Tooltip title={tx.skript_transaction_id} arrow>
                                            <Typography fontSize={11} color="#7a7a7a" sx={{ fontFamily: 'monospace', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {tx.skript_transaction_id}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>

                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={11} color="#7a7a7a" sx={{ fontFamily: 'monospace' }}>{tx.reference || '—'}</Typography>
                                    </TableCell>

                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={14} fontWeight={700} color="#10b981">${fmtAmt(tx.amount)}</Typography>
                                        <Box sx={{ display: 'inline-block', px: 1, py: 0.2, borderRadius: '4px', fontSize: 9, fontWeight: 700, color: '#f2674a', background: '#f2674a12', border: '1px solid #f2674a25', mt: 0.3 }}>
                                            {tx.currency}
                                        </Box>
                                    </TableCell>

                                    <TableCell sx={tdStyle}>
                                        {(tx as any).depositedAmount
                                            ? <>
                                                <Typography fontSize={14} fontWeight={700} color="#a78bfa">
                                                    ${fmtAmt((tx as any).depositedAmount)}
                                                </Typography>
                                                <Box sx={{ display: 'inline-block', px: 1, py: 0.2, borderRadius: '4px', fontSize: 9, fontWeight: 700, color: '#a78bfa', background: '#a78bfa12', border: '1px solid #a78bfa25', mt: 0.3 }}>
                                                    {(tx as any).depositedCurrency || 'USD'}
                                                </Box>
                                            </>
                                            : <Typography fontSize={11} color="#3a3a3a">—</Typography>
                                        }
                                    </TableCell>

                                    <TableCell sx={tdStyle}>
                                        {tx.type
                                            ? <Box sx={{ display: 'inline-block', px: 1.2, py: 0.3, borderRadius: '5px', fontSize: 10, fontWeight: 700, color: '#3b82f6', background: '#3b82f615', border: '1px solid #3b82f630' }}>{tx.type}</Box>
                                            : <Typography fontSize={11} color="#3a3a3a">—</Typography>
                                        }
                                    </TableCell>

                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={12} color="#9a9a9a">{tx.dataHolderName || '—'}</Typography>
                                    </TableCell>

                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={13} color="#e0e0e0">{fmtDate(tx.postingDateTime)}</Typography>
                                        <Typography fontSize={10} color="#5a5a5a">{fmtTime(tx.postingDateTime)}</Typography>
                                    </TableCell>

                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={13} color="#e0e0e0">{fmtDate(tx.processedAt)}</Typography>
                                        <Typography fontSize={10} color="#5a5a5a">{fmtTime(tx.processedAt)}</Typography>
                                    </TableCell>

                                    <TableCell sx={tdStyle}>
                                        <StatusPill status={tx.status} />
                                    </TableCell>

                                    <TableCell sx={{ ...tdStyle, maxWidth: 200 }}>
                                        {tx.errorMessage
                                            ? <Tooltip title={tx.errorMessage} arrow>
                                                <Typography fontSize={11} color="#ef4444" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180, cursor: 'help' }}>
                                                    ⚠ {tx.errorMessage}
                                                </Typography>
                                            </Tooltip>
                                            : <Typography fontSize={11} color="#3a3a3a">—</Typography>
                                        }
                                    </TableCell>

                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={11} align="center" sx={{ py: 8, background: '#1e1e1e' }}>
                                        <SyncAltIcon sx={{ color: '#2f2f2f', fontSize: 40, mb: 1 }} />
                                        <Typography color="#4a4a4a" fontSize={13}>No auto transactions found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={pagination.total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    showFirstButton showLastButton
                    sx={paginationStyle}
                />
            </Box>

            <PopupDialog
                open={errorPopup.open}
                onClose={() => setErrorPopup({ open: false, message: '' })}
                onConfirm={() => setErrorPopup({ open: false, message: '' })}
                variant="error"
                title="Something Went Wrong"
                message={errorPopup.message}
                confirmText="Try Again"
                cancelText="Dismiss"
            />
        </div>
    )
};

export default AutoTransfers;

const thStyle = {
    backgroundColor: '#1a1a1a', color: '#f2674a', fontSize: 11, fontWeight: 700,
    letterSpacing: '0.08em', textTransform: 'uppercase' as const, whiteSpace: 'nowrap' as const,
    py: 1.5, borderBottom: '1px solid #2a2a2a !important',
    position: 'sticky' as const, top: 0, zIndex: 2,
}
const tdStyle = { color: '#c0c0c0', py: 1.2, fontSize: 13, whiteSpace: 'nowrap' as const }
const trHover = { transition: 'background 0.15s', '&:hover': { background: '#ffffff05' } }
const searchFieldStyle = {
    background: 'transparent', borderRadius: '8px', border: '1px solid #333',
    transition: 'border-color 0.3s', '&:hover': { border: '1px solid #f2674a50' },
    '&:focus-within': { border: '1px solid #f2674a' },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& input': { color: '#fff', fontSize: 13 },
    '& input::placeholder': { color: '#555', opacity: 1 },
}
const paginationStyle = {
    color: '#6a6a6a', borderTop: '1px solid #252525',
    '& .MuiTablePagination-toolbar': { justifyContent: 'center' },
    '& .MuiTablePagination-selectLabel': { color: '#6a6a6a', margin: 0 },
    '& .MuiTablePagination-displayedRows': { color: '#6a6a6a', margin: 0 },
    '& .MuiSelect-select': { color: '#6a6a6a' },
    '& .MuiSelect-icon': { color: '#6a6a6a' },
    '& .MuiIconButton-root': { color: '#6a6a6a' },
    '& .MuiIconButton-root.Mui-disabled': { color: '#333' },
}